/*
 * @Descripttion: 「和谐相位搜索」纯前端计算规则（1:1 对齐老系统 PHP HomeController@search2）
 *
 *   老系统规则（web/yljx/app/Http/Controllers/HomeController.php:3593）：
 *     1. 由 start/end 生成区间内每一天，每天取北京时间 01:00；
 *     2. 取 日/月/水/金/火/木/土/天/海/冥 十颗行星黄经（瑞士星历）；
 *     3. 两两求角差（超过 180° 取 360−差）：
 *          - 落在 60°±9°  → `A*B`（六分相）
 *          - 落在 120°±9° → `A△B`（三分相）
 *          - 木星与土星几乎同度 → 「木土会局」
 *     4. 命中项数 ≥ num（前端「和谐相位」输入框）的日期才返回；
 *     5. 输出 {a:"yyyy-MM-dd", b:"日食；日*月；水△金"}，日月食标记排在相位命中项之前。
 *
 *   本模块只做「黄经 → 相位命中项 → 是否达标」的纯计算，不发起任何请求，
 *   便于单测（tests/harmonyPhaseSearch.test.js）与页面复用。
 *   黄经来源由页面调用星盘 astrolabe_micronaut 的 GET /api/planet-longitudes 提供。
 * @params:
 *   longitudes   [Number[]] 长度 10，下标即行星序号（0=日 … 9=冥），单位度
 *   num          [Number]   和谐相位阈值；<= 0 表示不设阈值（对齐老版未传 num 的恒真比较）
 * @Date: 2026-10-04
 */
const PLANET_NAMES = ['日', '月', '水', '金', '火', '木', '土', '天', '海', '冥']

/** 老版 $jingdu = 9：60°/120° 相位的容许度（度） */
const ASPECT_ORB = 9

const SOLAR_ECLIPSE_MARK = '日食'
const LUNAR_ECLIPSE_MARK = '月食'

/** 单次搜索允许的最大天数：逐日调用星盘接口，区间过大应显式拒绝而不是静默截断 */
const MAX_SEARCH_DAYS = 366

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * 两黄经的角差，超过 180° 取 360°−差（对齐老版 $cha 的计算）。
 * @param {Number} longitudeA 黄经 A（度）
 * @param {Number} longitudeB 黄经 B（度）
 * @returns {Number} 角差（0~180）
 */
function normalizeAngleDiff (longitudeA, longitudeB) {
  let diff = Math.abs(longitudeA - longitudeB)
  if (diff > 180) {
    diff = 360 - diff
  }
  return diff
}

/**
 * 计算某一天的相位命中项（含日月食标记，顺序与老版一致）。
 * @param {Number[]} longitudes 十颗行星的绝对黄经（下标即行星序号）
 * @param {String} solarEclipseDate 区间起点之后最近一次日食日期（yyyy-MM-dd），可为空
 * @param {String} lunarEclipseDate 区间起点之后最近一次月食日期（yyyy-MM-dd），可为空
 * @param {String} dateStr 当天日期（yyyy-MM-dd）
 * @returns {String[]} 命中项，例如 ['日食', '日*月', '水△金']
 */
function buildAspectItems (longitudes, solarEclipseDate, lunarEclipseDate, dateStr) {
  const items = []
  // 老版先把「日食」「月食」压入 $aa，再压相位命中项，顺序必须保持一致
  if (solarEclipseDate && dateStr === solarEclipseDate) {
    items.push(SOLAR_ECLIPSE_MARK)
  }
  if (lunarEclipseDate && dateStr === lunarEclipseDate) {
    items.push(LUNAR_ECLIPSE_MARK)
  }

  for (let i = 0; i < longitudes.length; i++) {
    for (let j = i + 1; j < longitudes.length; j++) {
      const diff = normalizeAngleDiff(longitudes[i], longitudes[j])
      if (diff > 60 - ASPECT_ORB && diff < 60 + ASPECT_ORB) {
        items.push(PLANET_NAMES[i] + '*' + PLANET_NAMES[j])
      } else if (diff > 120 - ASPECT_ORB && diff < 120 + ASPECT_ORB) {
        items.push(PLANET_NAMES[i] + '△' + PLANET_NAMES[j])
      } else if (PLANET_NAMES[i] === '木' && PLANET_NAMES[j] === '土' && diff === 0) {
        // 严格对齐老版 search2 的 $cha == 0（search() 用的是 <0.1，两者并不一致）
        items.push('木土会局')
      }
    }
  }
  return items
}

/**
 * 装配某一天的搜索记录。
 * @returns {{a: String, b: String}|null} 达标返回 {a:日期, b:相位串}；未达阈值返回 null
 */
function buildDayRow (dateStr, longitudes, num, solarEclipseDate, lunarEclipseDate) {
  const items = buildAspectItems(longitudes, solarEclipseDate, lunarEclipseDate, dateStr)
  if (num > 0 && items.length < num) {
    return null
  }
  return { a: dateStr, b: items.join('；') }
}

/**
 * 从星盘 /api/planet-longitudes 响应中提取按行星序号排列的黄经数组。
 * @param {Object} response 星盘响应 {planets:[{index,name,longitude,latitude}]}
 * @returns {{longitudes: Array<Number|null>, missing: Number[]}} missing 为缺失的行星序号
 */
function planetLongitudesFromResponse (response) {
  const longitudes = new Array(PLANET_NAMES.length).fill(null)
  const planets = (response && Array.isArray(response.planets)) ? response.planets : []
  planets.forEach(function (planet) {
    if (!planet) return
    const index = planet.index
    const longitude = planet.longitude
    if (Number.isInteger(index) && index >= 0 && index < longitudes.length && typeof longitude === 'number') {
      longitudes[index] = longitude
    }
  })
  const missing = []
  for (let i = 0; i < longitudes.length; i++) {
    if (longitudes[i] === null) {
      missing.push(i)
    }
  }
  return { longitudes: longitudes, missing: missing }
}

/** 从星盘 /api/eclipse/next-solar|next-lunar 响应中取出 nextDate（yyyy-MM-dd） */
function eclipseDateFromResponse (response) {
  if (!response || response.nextDate === undefined || response.nextDate === null) {
    return ''
  }
  return String(response.nextDate)
}

/** 把 yyyy-MM-dd 解析为 UTC 毫秒（避免本地时区导致跨天） */
function parseDateOnly (text) {
  const parts = String(text || '').split('-')
  if (parts.length !== 3) return NaN
  const year = Number(parts[0])
  const month = Number(parts[1])
  const day = Number(parts[2])
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return NaN
  if (month < 1 || month > 12 || day < 1 || day > 31) return NaN
  return Date.UTC(year, month - 1, day)
}

/** 把 UTC 毫秒格式化回 yyyy-MM-dd */
function formatDateOnly (ms) {
  const date = new Date(ms)
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return date.getUTCFullYear() + '-' + month + '-' + day
}

/**
 * 生成区间内的每一天（含首尾）。
 * @returns {String[]} yyyy-MM-dd 数组；入参非法或 end < start 时返回 []
 */
function enumerateDates (start, end) {
  const startMs = parseDateOnly(start)
  const endMs = parseDateOnly(end)
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
    return []
  }
  const dates = []
  for (let ms = startMs; ms <= endMs; ms += DAY_MS) {
    dates.push(formatDateOnly(ms))
  }
  return dates
}

/** 区间天数（含首尾）；入参非法或 end < start 时返回 0 */
function countDays (start, end) {
  return enumerateDates(start, end).length
}

/**
 * 对一批「日期 + 黄经」逐日判定，返回达标记录（按日期升序）。
 * @param {Array<{date: String, longitudes: Number[]}>} daySamples
 * @returns {Array<{a: String, b: String}>}
 */
function searchHarmonyRows (daySamples, num, solarEclipseDate, lunarEclipseDate) {
  const rows = []
  ;(daySamples || []).forEach(function (sample) {
    if (!sample || !Array.isArray(sample.longitudes)) return
    const row = buildDayRow(sample.date, sample.longitudes, num, solarEclipseDate, lunarEclipseDate)
    if (row) {
      rows.push(row)
    }
  })
  rows.sort(function (left, right) {
    return left.a < right.a ? -1 : (left.a > right.a ? 1 : 0)
  })
  return rows
}

module.exports = {
  PLANET_NAMES,
  ASPECT_ORB,
  SOLAR_ECLIPSE_MARK,
  LUNAR_ECLIPSE_MARK,
  MAX_SEARCH_DAYS,
  normalizeAngleDiff,
  buildAspectItems,
  buildDayRow,
  planetLongitudesFromResponse,
  eclipseDateFromResponse,
  enumerateDates,
  countDays,
  searchHarmonyRows
}
